import {
  motion,
} from "framer-motion";

import {
  useNavigate,
} from "react-router-dom";

import siteConfig from "../../../site/config/site";

import {
  getRoutePath,
} from "../../../site/config/routes";

/*
  ============================================================
  FLAG
  ============================================================
*/

function Flag({
  country,
}) {
  if (country === "fi") {
    return (
      <svg
        viewBox="0 0 18 12"
        className="
          h-3
          w-[18px]
          shrink-0
          overflow-hidden
          rounded-[2px]
        "
        aria-hidden="true"
      >
        <rect
          width="18"
          height="12"
          fill="#ffffff"
        />

        <rect
          x="5"
          width="3"
          height="12"
          fill="#003580"
        />

        <rect
          y="4.5"
          width="18"
          height="3"
          fill="#003580"
        />
      </svg>
    );
  }

  if (country === "gb") {
    return (
      <svg
        viewBox="0 0 18 12"
        className="
          h-3
          w-[18px]
          shrink-0
          overflow-hidden
          rounded-[2px]
        "
        aria-hidden="true"
      >
        <rect
          width="18"
          height="12"
          fill="#012169"
        />

        <path
          d="M0 0L18 12M18 0L0 12"
          stroke="#ffffff"
          strokeWidth="3"
        />

        <path
          d="M0 0L18 12M18 0L0 12"
          stroke="#C8102E"
          strokeWidth="1.2"
        />

        <path
          d="M9 0V12M0 6H18"
          stroke="#ffffff"
          strokeWidth="4"
        />

        <path
          d="M9 0V12M0 6H18"
          stroke="#C8102E"
          strokeWidth="2"
        />
      </svg>
    );
  }

  return null;
}

/*
  ============================================================
  LANGUAGE TOGGLE
  ============================================================

  appearance:

  "default"
  = tavallinen Ivory / Noir -käyttöliittymä

  "overlay"
  = hero-kuvan tai videon päällä käytettävä vaalea versio
*/

export default function LanguageToggle({
  currentLanguage = "fi",
  currentRouteId = "home",
  appearance = "default",
}) {
  const navigate =
    useNavigate();

  const languageConfig =
    siteConfig.languages ||
    {};

  const enabledLanguages =
    languageConfig.enabled ||
    [];

  const languageOptions =
    languageConfig.options ||
    {};

  const switcher =
    languageConfig.switcher ||
    {};

  const isOverlay =
    appearance === "overlay";

  function changeLanguage(
    nextLanguage
  ) {
    if (
      !enabledLanguages.includes(
        nextLanguage
      ) ||
      nextLanguage ===
        currentLanguage
    ) {
      return;
    }

    const nextPath =
      getRoutePath(
        currentRouteId,
        nextLanguage
      );

    navigate(nextPath);
  }

  if (
    enabledLanguages.length <
    2
  ) {
    return null;
  }

  return (
    <div
      className={
        isOverlay
          ? `
            relative
            inline-flex
            items-center
            gap-0.5
            rounded-full
            border
            border-white/25
            bg-black/15
            p-1
            shadow-sm
            backdrop-blur-xl
          `
          : `
            relative
            inline-flex
            items-center
            gap-0.5
            rounded-full
            border
            border-border
            bg-surface/60
            p-1
            shadow-sm
            backdrop-blur-xl
          `
      }
      role="group"
      aria-label={
        currentLanguage ===
        "en"
          ? "Language"
          : "Kieli"
      }
    >
      {enabledLanguages.map(
        (language) => {
          const option =
            languageOptions[
              language
            ] || {};

          const isActive =
            language ===
            currentLanguage;

          const code =
            option.code ||
            language.toUpperCase();

          const name =
            option.name ||
            code;

          return (
            <button
              key={language}
              type="button"
              onClick={() =>
                changeLanguage(
                  language
                )
              }
              className="
                relative
                flex
                h-8
                min-w-[54px]
                items-center
                justify-center
                gap-1.5
                rounded-full
                px-2.5
                text-[10px]
                font-semibold
                uppercase
                tracking-[0.14em]
                focus-visible:outline-none
              "
              aria-label={
                name
              }
              aria-pressed={
                isActive
              }
              title={name}
            >
              {isActive && (
                <motion.span
                  layoutId="premium-language-active"
                  className={
                    isOverlay
                      ? `
                        absolute
                        inset-0
                        rounded-full
                        border
                        border-white/20
                        bg-white/20
                      `
                      : `
                        absolute
                        inset-0
                        rounded-full
                        border
                        border-accent/30
                        bg-accent/20
                      `
                  }
                  transition={{
                    type: "spring",
                    stiffness: 340,
                    damping: 30,
                    mass: 0.75,
                  }}
                  aria-hidden="true"
                />
              )}

              <span
                className="
                  relative
                  z-10
                  flex
                  items-center
                  gap-1.5
                "
              >
                {switcher.showFlag !==
                  false && (
                  <Flag
                    country={
                      option.flag
                    }
                  />
                )}

                {switcher.showCode !==
                  false && (
                  <span
                    className={
                      isOverlay
                        ? isActive
                          ? "text-white"
                          : "text-white/65 transition-colors duration-300 hover:text-white"
                        : isActive
                          ? "text-text"
                          : "text-muted transition-colors duration-300 hover:text-text"
                    }
                  >
                    {code}
                  </span>
                )}
              </span>
            </button>
          );
        }
      )}
    </div>
  );
}
